import type { AgentId } from '@quintessence/core';

/**
 * Two colour sets per agent:
 *  - `brand`: the bright persona colours (social posts, and light on the site:
 *    glows, network hubs, signal pulses, element glyphs);
 *  - `series`: the same hues stepped down for data marks (legend dots, 2px
 *    accents, charts). Validated with the
 *    dataviz six-checks script in the chart order below (orange and gold are
 *    never neighbours): every adjacent pair clears CVD ΔE 12.3 and
 *    normal-vision ΔE 18.9, all ≥ 3:1 contrast on #111111 (the
 *    current #0c0e14 surface is darker, so contrast only rises).
 */
export const BRAND: Record<AgentId, string> = {
  pyre: '#ff6b2c',
  hoard: '#f5c542',
  rain: '#a78bfa',
  tide: '#2dd4bf',
};

export const SERIES: Record<AgentId, string> = {
  pyre: '#e8591c',
  rain: '#8b6ff0',
  hoard: '#b98a0c',
  tide: '#0e9a8a',
};

/** Stacking / legend order for multi-series charts (keeps orange and gold apart). */
export const CHART_ORDER: AgentId[] = ['pyre', 'rain', 'hoard', 'tide'];

/** Marker shapes give a second identity channel where agents share one plot. */
export const SHAPE: Record<AgentId, 'circle' | 'square' | 'diamond' | 'triangle'> = {
  pyre: 'circle',
  rain: 'diamond',
  hoard: 'square',
  tide: 'triangle',
};

/** Neutral inks; mirrors the CSS tokens. Single-series charts draw in `accent` (near-white). */
export const INK = {
  surface: '#0c0e14',
  primary: '#eff1f7',
  secondary: '#a5aabb',
  muted: '#7b8096',
  grid: '#171a24',
  axis: '#262a37',
  good: '#3fb950',
  critical: '#e5534b',
  accent: '#e6e8f0',
};

/** Diverging blue ↔ red with a neutral midpoint, stepped for the dark surface. */
export function diverging(v: number, maxAbs: number): string {
  if (!Number.isFinite(v) || maxAbs <= 0) return '#22252f';
  const t = Math.max(-1, Math.min(1, v / maxAbs));
  const steps = 5;
  const k = Math.round(Math.abs(t) * steps) / steps;
  const mid: [number, number, number] = [34, 37, 47];
  const pos: [number, number, number] = [57, 135, 229];
  const neg: [number, number, number] = [230, 103, 103];
  const end = t >= 0 ? pos : neg;
  const c = mid.map((m, i) => Math.round(m + (end[i] - m) * k));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

/** Ink colour for text placed inside a filled cell. */
export function inkOn(fill: string): string {
  const m = fill.match(/\d+/g);
  if (!m) return INK.primary;
  const [r, g, b] = m.map(Number);
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 140 ? '#07080c' : INK.primary;
}
